/**
 * PeekPanel — side panel for quick detail view.
 * Per app-codex: surface "peek.panel". Behaviour: peek.open, peek.close, peek.navigate.
 */
import { useApp } from '../store'
import { X, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { fetchTasks, type DbTask } from '../db'
import { devDb } from '../devMode'

export function PeekPanel() {
  const { peek, closePeek, dev } = useApp()
  const [task, setTask] = useState<DbTask | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (peek.kind !== 'task' || !peek.id) {
      setTask(null)
      return
    }
    setLoading(true)
    ;(async () => {
      const { data } = dev ? await devDb.tasks.list() : await fetchTasks('family')
      const found = (data ?? []).find((t) => t.id === peek.id) ?? null
      setTask(found)
      setLoading(false)
    })()
  }, [peek.kind, peek.id, dev])

  // Escape closes peek
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && peek.kind) closePeek()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [peek.kind, closePeek])

  if (!peek.kind) return null

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white border-l border-slate-200 shadow-xl z-30 overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <h3 className="font-medium text-skin-text capitalize">{peek.kind}</h3>
        <button
          onClick={closePeek}
          className="p-2 rounded-lg hover:bg-slate-100 min-h-touch min-w-touch"
          aria-label="Close peek"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">
        {peek.kind === 'task' && (
          loading ? (
            <div className="text-slate-500 text-sm">Loading task…</div>
          ) : task ? (
            <div className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold text-skin-text">{task.title}</h2>
                <p className="text-xs text-slate-500 mt-1">id: {task.id}</p>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {task.status.replace('_', ' ')}
                </span>
                <span className="inline-flex items-center gap-1 text-slate-600">
                  <Sparkles className="w-3 h-3 text-reward-500" /> {task.ep} EP
                </span>
                <span className="inline-flex items-center gap-1 text-slate-600 capitalize">
                  {task.type}
                </span>
              </div>
              {task.assignees.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide">For</h4>
                  <p className="text-sm text-skin-text mt-1">
                    {task.assignees.map((a) => a.replace(/^dev-/, '')).join(', ')}
                  </p>
                </div>
              )}
              <div>
                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide">Notes</h4>
                <p className="text-sm text-slate-600 mt-1 italic">
                  (Edit-in-place coming in a future release)
                </p>
              </div>
            </div>
          ) : (
            <div className="text-slate-500 text-sm">Task not found.</div>
          )
        )}
        {peek.kind !== 'task' && (
          <div className="text-slate-500 text-sm">Loading {peek.kind}…</div>
        )}
      </div>
    </div>
  )
}